import React, { useEffect, useState, useRef } from 'react';

interface EpureLoaderProps {
  onLoadingComplete?: () => void;
  minDisplayTime?: number;
}

const particles = [
  { x: '12%', y: '18%', size: 10, color: 'rose', blur: 6, opacity: 0.18, delay: 0, duration: 6 },
  { x: '85%', y: '25%', size: 6,  color: 'lilas', blur: 4, opacity: 0.15, delay: 1.2, duration: 7.5 },
  { x: '70%', y: '75%', size: 12, color: 'rose', blur: 8, opacity: 0.12, delay: 0.6, duration: 8 },
  { x: '25%', y: '80%', size: 8,  color: 'lilas', blur: 5, opacity: 0.2, delay: 2, duration: 6.5 },
  { x: '50%', y: '10%', size: 5,  color: 'rose', blur: 4, opacity: 0.14, delay: 0.8, duration: 7 },
  { x: '90%', y: '60%', size: 7,  color: 'lilas', blur: 6, opacity: 0.16, delay: 1.5, duration: 8.5 },
  { x: '8%',  y: '55%', size: 9,  color: 'rose', blur: 5, opacity: 0.1, delay: 2.5, duration: 7 },
];

export const EpureLoader: React.FC<EpureLoaderProps> = ({ 
  onLoadingComplete, 
  minDisplayTime = 2000 
}) => {
  const [phase, setPhase] = useState<'text' | 'bar' | 'exit' | 'done'>('text');
  const onLoadingCompleteRef = useRef(onLoadingComplete);
  onLoadingCompleteRef.current = onLoadingComplete;


  useEffect(() => {
    const t1 = setTimeout(() => setPhase('bar'), 400);
    const t2 = setTimeout(() => setPhase('exit'), minDisplayTime + 200);
    const t3 = setTimeout(() => {
      setPhase('done');
      onLoadingCompleteRef.current?.();
    }, minDisplayTime + 700);

    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [minDisplayTime]);

  if (phase === 'done') return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-dark"
      role="status"
      aria-live="polite"
      aria-label="Chargement en cours"
      style={{
        opacity: phase === 'exit' ? 0 : 1,
        transition: 'opacity 500ms ease-out',
      }}
    >
      <span className="sr-only">Chargement de l'application en cours</span>

      {/* Particules flottantes */}
      {particles.map((p, i) => (
        <div
          key={i}
          className="absolute rounded-full pointer-events-none animate-float"
          style={{
            left: p.x,
            top: p.y,
            width: p.size,
            height: p.size,
            background: `hsl(var(--${p.color}))`,
            opacity: p.opacity,
            filter: `blur(${p.blur}px)`,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
          }}
        />
      ))}

      <h1
        className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-off-white"
        style={{
          opacity: phase === 'text' ? 0 : 1,
          transition: 'opacity 400ms ease-out',
        }}
      >
        GLOW LAB
      </h1>

      <div className="mt-6 w-[120px] h-[2px] rounded-full bg-border overflow-hidden">
        <div
          className="h-full bg-rose rounded-full will-change-[width]"
          style={{
            width: phase === 'bar' || phase === 'exit' ? '100%' : '0%',
            transition: phase === 'bar' ? 'width 1.6s ease-in-out' : 'none',
            boxShadow: '0 0 8px hsl(320 50% 78% / 0.4)',
          }}
        />
      </div>

      {/* Tagline */}
      <p
        className="mt-5 font-body text-xs sm:text-sm text-grey tracking-wide"
        style={{
          opacity: phase === 'bar' || phase === 'exit' ? 1 : 0,
          transition: 'opacity 600ms ease-out',
          transitionDelay: '300ms',
        }}
      >
        Stratégie digitale sur mesure
      </p>
    </div>
  );
};

export default EpureLoader;
